import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useObserver } from 'mobx-react';
import { withNamespaces } from 'react-i18next';
import { useMemoOne } from 'use-memo-one';

import { routeNode } from 'core/routing';

import DocumentHead from 'legacyComponents/documentHead';

import { useRouterStore, useClientStore, useAppManager } from 'apps/hooks';
import { FourOhFour } from 'apps/fourOhFour/index';
import Header from 'apps/components/header/header';
import { MainWrapper } from 'legacyComponents/styled/lib';
import { SectionBanner, Paragraph, Box } from 'components';

import { ContributionPage } from 'apps/repository/contributionPage/index';
import { RepoAppManager } from 'apps/repository/appManager';
import { IssuePage } from 'apps/issueTracker/views/issuePage';
import { imageSrc } from 'apps/file/utils';
import { stripTags } from 'apps/utils';
import { conf as globalConf } from 'apps/config.yaml';
import { ProjectName } from 'apps/project/views/projectName';
import StatusMessage from 'apps/project/views/statusMessage';
import ImportLoading from 'apps/project/views/importLoading';
import { ProjectAppManager } from 'apps/project/appManager';
import { PaymentSpaceApp } from 'apps/payment/app';
import { ProjectHome } from 'apps/project/tabs/home';
import { frameStore } from 'apps/components/Frame/appManager';

import {
  ProfileFilesRouter,
  ProfileFilesVersionRouter,
  InitiativeFilesRouter,
  InitiativeFilesVersionRouter,
} from 'apps/project/tabs/files/router';

import { FilePage } from 'apps/project/tabs/file/filePage';
import { ConflictsPage } from 'apps/project/tabs/conflicts/index';
import { IssuesPage } from 'apps/project/tabs/issues';
import { ProjectTabsBar } from 'apps/project/views/tabsBar';
import {
  ProfileProjectSettingsRouter,
  InitiativeProjectSettingsRouter,
} from 'apps/project/settings/router';
import { StatButtons } from 'apps/project/views/statButtons';
import { SubscriptionAppManager } from 'apps/payment/subscription/appManager';

import { Loading } from 'libs/rendering/gltf/components/loading';

import { BANNER_ACTIONS } from 'constants/analytics';
import { analytics } from 'core/analytics';

import { ProjectNameWrapper } from './styles';

export const generateFileViewsMap = (spaceType, version = false) => {
  // Instead of being looked up in the views Map, "FilePage" is currently
  // being "short-circuited" to in the ProjectSpace router based on
  // "atFilePage", in order to render the full screen file page without other
  // project page components above it (Header, ProjectName, ProjectTabsBar etc).
  // Have kept in in the ViewsMap as a reminder, or in case we refactor these
  // views in the future.
  if (version) {
    return {
      'version.files':
        spaceType === 'initiative'
          ? InitiativeFilesVersionRouter
          : ProfileFilesVersionRouter,
      'version.file': FilePage, // * see comment above.
      version: ProjectHome,
    };
  }
  return {
    files:
      spaceType === 'initiative' ? InitiativeFilesRouter : ProfileFilesRouter,
    file: FilePage, // * see comment above.
  };
};

const commonProjectViews = {
  '': ProjectHome,
  issues: IssuesPage,
  issue: IssuePage,
  contribution: ContributionPage,
  conflicts: ConflictsPage,
};

const profileViewsMap = {
  ...commonProjectViews,
  ...generateFileViewsMap('profile'),
  ...generateFileViewsMap('profile', true),
  settings: ProfileProjectSettingsRouter,
};

const initiativeViewsMap = {
  ...commonProjectViews,
  ...generateFileViewsMap('initiative'),
  ...generateFileViewsMap('initiative', true),
  settings: InitiativeProjectSettingsRouter,
};

function isFileRoute(route) {
  if (
    route.name.endsWith('project.file') ||
    route.name.endsWith('project.version.file')
  ) {
    return true;
  }
  return false;
}

const ProjectSpaceBanner = withNamespaces()(props => (
  <MainWrapper>
    {props.signedInProfile ? (
      !props.isMobile && (
        <SectionBanner
          bannerId='project-fork-remix'
          imageSrc='/assets/banners/005.svg'
          title={props.t(
            'Find the best manufacturer for your part, every time.',
          )}
          buttonText={props.t('Get a Quote')}
          linkName='https://wikifactory.com/make-part'
          paddingTop='large'
          paddingBottom='xlarge'
          eventCallback={() =>
            analytics.sendEvent(BANNER_ACTIONS.PROJECT_SIGNED_IN)
          }
        >
          <Box>
            <Paragraph>
              Identifying the best manufacturers for your parts is hard, but
              <strong> Wikifactory has you covered</strong>.
            </Paragraph>
            <Paragraph>
              Wikifactory Marketplace has{' '}
              <strong>
                hand-picked the world’s best, quality-assured manufacturers
              </strong>
              , so you don’t have to.
            </Paragraph>
          </Box>
        </SectionBanner>
      )
    ) : (
      <SectionBanner
        bannerId='project-collaborate-cad'
        imageSrc='/assets/banners/005.svg'
        title={props.t('Experience Collaborative Manufacturing.')}
        description={props.t(`Share, iterate, prototype and manufacture your hardware products completely online.
         Explore Wikifactory, adopted by a global community of over 140,000 engineers, designers, SMEs and hardware startups covering industries from robotics and drones to electric vehicles, 3D printers, bio-tech, agri-tech, and smart furniture.`)}
        buttonText={props.t('Learn more')}
        linkName='https://wikifactory.com'
        paddingTop='large'
        paddingBottom='xlarge'
        eventCallback={() =>
          analytics.sendEvent(BANNER_ACTIONS.PROJECT_SIGNED_OUT)
        }
      >
        <Box>
          <Paragraph>
            <strong>Share, iterate, prototype</strong> and{' '}
            <strong>manufacture</strong> your hardware products completely
            online.
          </Paragraph>
          <Paragraph>
            Adopted by a <strong>global community of over 140,000</strong>{' '}
            engineers, designers, SMEs and hardware startups.
          </Paragraph>
          <Paragraph>
            <strong>Driving innovation across industries</strong> from robotics
            and drones to electric vehicles, 3D printers, bio-tech, agri-tech,
            and smart furniture.
          </Paragraph>
        </Box>
      </SectionBanner>
    )}
  </MainWrapper>
));

const ProjectSpace = props => {
  const clientStore = useClientStore();
  const { route, params } = useRouterStore();
  const atFilePage = isFileRoute(route);

  const projectManager = useAppManager(
    ProjectAppManager,
    null,
    {
      ...props,
      params,
    },
    !atFilePage,
  );
  const repoManager = useAppManager(
    RepoAppManager,
    projectManager,
    {
      ...props,
      params,
    },
    !!projectManager,
  );
  const spaceSlug = route.params.profile || route.params.initiative;
  const subscriptionManager = useAppManager(
    SubscriptionAppManager,
    projectManager,
    {
      ...props,
      params,
      spaceSlug,
    },
    !!projectManager,
  );
  // TODO: the following is actually dead code? ProjectTabsBar does not really use it
  const payment = useMemoOne(() => {
    if (!subscriptionManager) return null;
    const app = new PaymentSpaceApp(subscriptionManager, {
      ...props,
      params,
      spaceSlug,
    });
    return app;
  }, [subscriptionManager]);

  return useObserver(() => {
    const project = projectManager?.project;

    useEffect(() => {
      frameStore.SetProject(project);
      return () => {
        frameStore.SetProject(null);
      };
    }, [project]);

    // render the full screen file page without other project components above it.
    if (atFilePage) return <FilePage params={params} />;

    const profileName = params.get('profile');
    const initiativeName = params.get('initiative');

    if (projectManager?.notFound) return <FourOhFour type='Project' />;

    // Display a 404 page if we don't have permissions to see settings.
    // Bad solution, actual solution would be to check whether the View that is going to be rendered
    // by the inner router is a 404 component, in which case we don't render everything else around the <View />
    // just it. I don't know that we have a good way to detect 404 in the nested routers.
    if (
      route.name.includes('settings') &&
      project !== null &&
      !project.canUpdate
    ) {
      return <FourOhFour type='Project' />;
    }

    if (!project) return <Loading visible noText />; // Note: This must short circuits the project layout loader!

    const formStore = project?.editing ? project?.editFormStore : null;

    const View = props.componentFromRoute();
    return (
      <>
        {project !== null && (
          <DocumentHead
            title={`${project.name} by ${profileName || initiativeName}`}
            description={stripTags(project.description)}
            imagePath={imageSrc(
              project.image,
              '',
              globalConf.imgSizes.project.large,
            )}
          />
        )}
        <Header />
        <ProjectSpaceBanner
          signedInProfile={clientStore.signedInProfile}
          isMobile={clientStore.isMobile}
        />
        <ProjectNameWrapper>
          <ProjectName app={project} fieldName='name' formStore={formStore} />
          {project && <StatButtons projectManager={projectManager} />}
        </ProjectNameWrapper>
        {project?.isImporting ? (
          <>
            <StatusMessage
              status={!projectManager.retryToken && project.importStatus.status}
              tryAgain={projectManager.ShowAuthModal}
              cancelImport={projectManager.CancelImportAction}
            />
            <ImportLoading
              status={!projectManager.retryToken && project.importStatus.status}
              generalProgress={project.importStatus.generalProgress}
              stepProgress={project.importStatus.generalProgress}
            />
          </>
        ) : (
          <>
            <ProjectTabsBar
              route={route}
              project={project}
              payment={payment}
              repoManager={repoManager}
              subscriptionManager={subscriptionManager}
            />
            <View
              params={params}
              project={project}
              projectManager={projectManager}
              repoManager={repoManager}
            />
            <div id='post-wrapper-section' />
          </>
        )}
      </>
    );
  });
};

export const ProfileProjectRouter = routeNode(
  'profile.project',
  profileViewsMap,
  FourOhFour,
)(ProjectSpace);
export const InitiativeProjectRouter = routeNode(
  'initiative.project',
  initiativeViewsMap,
  FourOhFour,
)(ProjectSpace);

ProjectSpace.propTypes = {
  componentFromRoute: PropTypes.func.isRequired,
};
